import { Badge, Button, Card, formatDate, Icon, SegmentedControl, useToast, type Column } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { exportBills, listBills, type BillSummary } from "../../api/billing";
import { isApiError, newIdempotencyKey } from "../../api/client";
import { exportInvoices, listCreditNotes, listInvoices, type CreditNoteSummary, type InvoiceSummary } from "../../api/invoices";
import { executeRefund, listClassRefundsDue, listPayments, listRefunds, type Refund } from "../../api/payments";
import type { BillPayment } from "../../api/billing";
import { NoAccess } from "../../app/NoAccess";
import { areaAllows } from "../../app/nav";
import { today } from "../../lib/dates";
import { useQuery } from "@tanstack/react-query";
import { allPages, downloadCsv, toCsv } from "./csv";
import { useAppContext, useMoney, usePaged, useScope } from "./hooks";
import { PageHead, PagedTable, QueryFailure, ListSkeleton } from "./states";
import { billing as t } from "./strings";
import s from "./billing.module.css";

type Tab = "bills" | "payments" | "invoices" | "creditNotes" | "refunds";
const TABS: Tab[] = ["bills", "payments", "invoices", "creditNotes", "refunds"];

export const payTone = (st: string) => (st === "paid" ? "ok" : st === "partial" ? "warn" : st === "unpaid" ? "crit" : st === "void" ? "neutral" : "info") as "ok" | "warn" | "crit" | "neutral" | "info";

export function BillingPage() {
  const ctx = useAppContext();
  const [sp, setSp] = useSearchParams();
  const raw = sp.get("tab");
  const tab: Tab = TABS.includes(raw as Tab) ? (raw as Tab) : "bills";
  if (ctx.status !== "loading" && !areaAllows("billing", ctx.has)) return <NoAccess />;
  const tabs = TABS.filter((k) => k !== "refunds" || ctx.has("revenue.refunds_view")).filter((k) => k !== "payments" || ctx.has("billing.payments_view"));
  return (
    <div className={s.page}>
      <title>Billing · Helmr</title>
      <PageHead title={t.title} subtitle={t.subtitle} action={ctx.has("billing.create_bill") ? <Button as={Link} to="/billing/new" variant="primary"><Icon name="plus" />{t.newBill}</Button> : undefined} />
      <SegmentedControl label="Billing sections" value={tab} onChange={(v) => setSp({ tab: v }, { replace: true })} options={tabs.map((k) => ({ value: k, label: t.tabs[k] }))} />
      <Card>
        {tab === "bills" ? <BillsTab /> : tab === "payments" ? <PaymentsTab /> : tab === "invoices" ? <InvoicesTab /> : tab === "creditNotes" ? <CreditNotesTab /> : <RefundsTab />}
      </Card>
    </div>
  );
}

function ExportButton({ run }: { run: () => Promise<number> }) {
  const toast = useToast();
  const m = useMutation({ mutationFn: run, onSuccess: (n) => toast.show(t.exportDone(n)), onError: (e) => toast.show(isApiError(e) ? e.message : "Export failed") });
  return <Button size="sm" onClick={() => m.mutate()} busy={m.isPending} busyLabel="Exporting…">{t.export}</Button>;
}

/** Each export is its own lever (D57): bills and invoices are separate downloads. */
function useCanExport(lever: "exports.bills" | "exports.invoices") {
  const ctx = useAppContext();
  return ctx.has(lever);
}

function BillsTab() {
  const ctx = useAppContext();
  const { client, now, scopeKey } = useScope();
  const nav = useNavigate();
  const { fmt } = useMoney(ctx);
  const [status, setStatus] = useState<"all" | "unpaid" | "partial" | "paid" | "void">("all");
  const [when, setWhen] = useState<"today" | "all">("today");
  const day = today(now(), ctx.timeZone);
  const filters = { ...(when === "today" ? { from: day, to: day } : {}), ...(status === "void" ? { status: "void" } : status === "all" ? {} : { paymentStatus: status }) };
  const q = usePaged(["bills", ...scopeKey, filters], async (cursor) => {
    const r = await listBills(client, { ...filters, cursor });
    return { rows: r.bills, next: r.nextCursor };
  });
  const rev = ctx.has("revenue.bill_amounts");
  const cols: Column<BillSummary>[] = [
    { key: "id", header: t.cols.bill, cell: (b) => <span className={s.num}>{b.id.slice(-6).toUpperCase()}</span> },
    { key: "m", header: t.cols.member, cell: (b) => <span className={s.wrap2}>{b.buyerName ?? "Walk-in"}</span> },
    { key: "d", header: t.cols.date, cell: (b) => formatDate(b.businessDate, ctx.locale, "short") },
    ...(rev ? [
      { key: "t", header: t.cols.total, align: "end" as const, cell: (b: BillSummary) => <span className={s.num}>{fmt(b.total)}</span> },
      { key: "b", header: t.cols.balance, align: "end" as const, cell: (b: BillSummary) => <span className={s.num}>{fmt(b.balance)}</span> },
    ] : []),
    { key: "s", header: t.cols.status, cell: (b) => { const k = b.status === "void" ? "void" : b.paymentStatus; return <Badge tone={payTone(k)} dot>{t.payStatus[k]}</Badge>; } },
  ];
  return (
    <>
      <div className={s.bar}>
        <SegmentedControl label="Bill status" value={status} onChange={setStatus} options={(["all", "unpaid", "partial", "paid", "void"] as const).map((v) => ({ value: v, label: t.filters[v] }))} />
        <div style={{ display: "flex", gap: 8 }}>
          <SegmentedControl label="Date" value={when} onChange={setWhen} options={[{ value: "today", label: "Today" }, { value: "all", label: "All dates" }]} />
          {useCanExport("exports.bills") ? <ExportButton run={async () => {
            const rows = await allPages(async (cursor) => { const r = await exportBills(client, { ...filters, cursor }); return { rows: r.bills, next: r.nextCursor }; });
            // Amounts are the API's integer minor units, unconverted; left out without revenue.bill_amounts, as on the list.
            downloadCsv(`bills-${day}.csv`, toCsv([
              ["bill", "member", "date", "status", "payment_status", ...(rev ? ["total_minor", "balance_minor", "currency"] : [])],
              ...rows.map((b) => [b.id, b.buyerName, b.businessDate, b.status, b.paymentStatus, ...(rev ? [b.total, b.balance, b.currency] : [])]),
            ]));
            return rows.length;
          }} /> : null}
        </div>
      </div>
      <PagedTable query={q} columns={cols} rowKey={(b) => b.id} onRowClick={(b) => nav(`/billing/bills/${b.id}`)} label="Bills" empty={when === "today" ? t.empty.bills : ["No bills match", "Try another filter."]} what="bills" />
    </>
  );
}

function PaymentsTab() {
  const ctx = useAppContext();
  const { client, scopeKey } = useScope();
  const { fmt } = useMoney(ctx);
  const [method, setMethod] = useState("");
  const q = usePaged(["payments", ...scopeKey, method], async (cursor) => {
    const r = await listPayments(client, { method: method || undefined, cursor });
    return { rows: r.payments, next: r.nextCursor };
  });
  const cols: Column<BillPayment>[] = [
    { key: "d", header: t.cols.date, cell: (p) => formatDate(p.businessDate, ctx.locale, "short") },
    { key: "m", header: t.cols.method, cell: (p) => t.methods[p.method] ?? p.method },
    ...(ctx.has("revenue.bill_amounts") ? [{ key: "a", header: t.cols.amount, align: "end" as const, cell: (p: BillPayment) => <span className={s.num}>{fmt(p.amount)}</span> }] : []),
    { key: "b", header: t.cols.bill, cell: (p) => <Link to={`/billing/bills/${p.billId}`}>{p.billId.slice(-6).toUpperCase()}</Link> },
  ];
  return (
    <>
      <div className={s.bar}>
        <label className={s.field}>{t.filters.method}
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">{t.filters.allMethods}</option>
            {Object.entries(t.methods).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
      </div>
      <PagedTable query={q} columns={cols} rowKey={(p) => p.id} label="Payments" empty={t.empty.payments} what="payments" />
    </>
  );
}

function InvoicesTab() {
  const ctx = useAppContext();
  const { client, now, scopeKey } = useScope();
  const nav = useNavigate();
  const { fmt } = useMoney(ctx);
  const q = usePaged(["invoices", ...scopeKey], async (cursor) => {
    const r = await listInvoices(client, { cursor });
    return { rows: r.invoices, next: r.nextCursor };
  });
  const rev = ctx.has("revenue.bill_amounts");
  const cols: Column<InvoiceSummary>[] = [
    { key: "n", header: t.cols.invoice, cell: (i) => <span className={s.num}>{i.number}</span> },
    { key: "d", header: t.cols.date, cell: (i) => formatDate(i.issueDate, ctx.locale, "short") },
    ...(ctx.has("revenue.bill_amounts") ? [{ key: "t", header: t.cols.total, align: "end" as const, cell: (i: InvoiceSummary) => <span className={s.num}>{fmt(i.total)}</span> }] : []),
  ];
  return (
    <>
      {useCanExport("exports.invoices") ? <div className={s.bar}><span /><ExportButton run={async () => {
        const rows = await allPages(async (cursor) => { const r = await exportInvoices(client, { cursor }); return { rows: r.invoices, next: r.nextCursor }; });
        downloadCsv(`invoices-${today(now(), ctx.timeZone)}.csv`, toCsv([
          ["invoice", "bill", "date", "type", ...(rev ? ["taxable_minor", "cgst_minor", "sgst_minor", "igst_minor", "round_off_minor", "total_minor", "currency"] : [])],
          ...rows.map((i) => [i.number, i.billId, i.issueDate, i.docType, ...(rev ? [i.taxableTotal, i.cgstTotal, i.sgstTotal, i.igstTotal, i.roundOff, i.total, i.currency] : [])]),
        ]));
        return rows.length;
      }} /></div> : null}
      <PagedTable query={q} columns={cols} rowKey={(i) => i.id} onRowClick={(i) => nav(`/billing/invoices/${i.id}`)} label="Invoices" empty={t.empty.invoices} what="invoices" />
    </>
  );
}

function CreditNotesTab() {
  const ctx = useAppContext();
  const { client, scopeKey } = useScope();
  const nav = useNavigate();
  const { fmt } = useMoney(ctx);
  const q = usePaged(["credit-notes", ...scopeKey], async (cursor) => {
    const r = await listCreditNotes(client, { cursor });
    return { rows: r.creditNotes, next: r.nextCursor };
  });
  const cols: Column<CreditNoteSummary>[] = [
    { key: "n", header: t.cols.reason, cell: (c) => <span className={s.num}>{c.number}</span> },
    { key: "d", header: t.cols.date, cell: (c) => formatDate(c.issueDate, ctx.locale, "short") },
    { key: "s", header: t.cols.settlement, cell: (c) => t.settlements[c.settlement] ?? c.settlement },
    ...(ctx.has("revenue.bill_amounts") ? [{ key: "t", header: t.cols.total, align: "end" as const, cell: (c: CreditNoteSummary) => <span className={s.num}>{fmt(c.total)}</span> }] : []),
  ];
  return <PagedTable query={q} columns={cols} rowKey={(c) => c.id} onRowClick={(c) => nav(`/billing/credit-notes/${c.id}`)} label="Credit notes" empty={t.empty.creditNotes} what="credit notes" />;
}

function RefundsTab() {
  const ctx = useAppContext();
  const { client, scopeKey } = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const { fmt } = useMoney(ctx);
  const canExecute = ctx.has("revenue.refund");
  const q = usePaged(["refunds", ...scopeKey], async (cursor) => {
    const r = await listRefunds(client, { cursor });
    return { rows: r.refunds, next: r.nextCursor };
  });
  const classQ = useQuery({ queryKey: ["class-refunds", ...scopeKey], queryFn: () => listClassRefundsDue(client), enabled: ctx.has("classes.refunds_due"), retry: false });
  const [busy, setBusy] = useState<string | null>(null);
  // One key per refund row, kept across clicks and retries so a dropped response never sends a refund twice.
  const [keys] = useState(() => new Map<string, string>());
  const run = async (r: Refund) => {
    setBusy(r.id);
    if (!keys.has(r.id)) keys.set(r.id, newIdempotencyKey());
    try {
      await executeRefund(client, r.id, keys.get(r.id)!);
      keys.delete(r.id);
      toast.show(t.executed);
      await qc.invalidateQueries({ queryKey: ["refunds"] });
    } catch (e) {
      toast.show(isApiError(e) ? e.message : t.errors.generic);
    } finally { setBusy(null); }
  };
  const cols: Column<Refund>[] = [
    { key: "d", header: t.cols.date, cell: (r) => new Intl.DateTimeFormat(ctx.locale, { day: "numeric", month: "short", timeZone: ctx.timeZone }).format(r.createdAt) },
    { key: "m", header: t.cols.method, cell: (r) => (r.method === "gateway" ? "Gateway" : t.methods[r.method] ?? r.method) },
    ...(ctx.has("revenue.bill_amounts") ? [{ key: "a", header: t.cols.amount, align: "end" as const, cell: (r: Refund) => <span className={s.num}>{fmt(r.amount)}</span> }] : []),
    { key: "s", header: t.cols.status, cell: (r) => <Badge tone={r.status === "processed" ? "ok" : r.status === "failed" ? "crit" : "warn"} dot>{t.refundStatus[r.status]}</Badge> },
    { key: "x", header: t.execute, srOnly: true, align: "end", cell: (r) => (canExecute && (r.status === "pending" || r.status === "failed") ? <Button size="sm" onClick={() => void run(r)} busy={busy === r.id} busyLabel="Sending…">{t.execute}</Button> : null) },
  ];
  return (
    <>
      <PagedTable query={q} columns={cols} rowKey={(r) => r.id} label="Refunds" empty={t.empty.refunds} what="refunds" />
      {classQ.isPending && ctx.has("classes.refunds_due") ? <ListSkeleton rows={2} /> : null}
      {classQ.error ? <QueryFailure error={classQ.error} what="class refunds" onRetry={() => void classQ.refetch()} /> : null}
      {classQ.data && classQ.data.length > 0 ? (
        <>
          <div className={s.bucket}>{t.classRefunds}</div>
          {classQ.data.map((c) => (
            <div key={c.id} className={s.dueRow}>
              <span className={s.nm}>{c.memberName ?? c.memberId}</span>
              <span className={s.muted}>{t.classRefundRow(c.className, formatDate(c.date, ctx.locale, "short"))}</span>
            </div>
          ))}
        </>
      ) : null}
    </>
  );
}
